import { useState } from "react";
import { Upload, Loader2, Sparkles, AlertTriangle, X } from "lucide-react";
import { FiZap, FiMic, FiVideo } from "react-icons/fi";
import API from "../../api";
import { useNavigate } from "react-router-dom";

export default function Hook({ user }) {
    const [video, setVideo] = useState(null);
    const [preview, setPreview] = useState(null);
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);
    const [error, setError] = useState(null);
    const [showTokenModal, setShowTokenModal] = useState(false);

    const navigate = useNavigate();

    const handleVideoUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setVideo(file);
    setPreview(URL.createObjectURL(file));
    setResult(null);
    setError(null);
    };

    const handleAnalyze = async () => {
    if (!user?.tokens || user.tokens <= 0) {
        setShowTokenModal(true);
        return;
    }
    
    if (!video) return;
    setLoading(true);
    setResult(null);
    setError(null);
    
    try {
        const formData = new FormData();
        formData.append("video", video);
        formData.append("userId", user.id); // ✅ ENVOI DE L'ID USER
    
        const response = await API.post("/analyze-hook", formData, {
        headers: { "Content-Type": "multipart/form-data" },
        });
    
        const data = response.data;


        if (response.status === 429) {
            setError("⚡ You're analyzing too fast. Please wait a few seconds before trying again.");
            return;
        }
    
        if (data.success) {
        console.log(data)
        setResult(data);
    
        // ✅ MET À JOUR LES TOKENS EN LOCAL APRÈS SUCCÈS
        if (data.tokens !== undefined) {
            user.tokens = data.tokens; 
        }
    
        } else {
        throw new Error("Unexpected response format");
        }
    } catch (err) {
        console.error("❌ Error analyzing hook:", err);
        setError("An unexpected server error occurred. Please try again later.");
    } finally {
        setLoading(false);
    }
    };
    

    return (
        <div className="min-h-screen bg-[#0D1117] text-[#E6EDF3] flex flex-col items-center px-4 py-16">
    
        {/* HEADER */}
        <div className="text-center max-w-2xl mb-12">
            <h1 className="text-4xl sm:text-5xl font-bold">
            Test your{" "}
            <span className="bg-gradient-to-r from-[#3B82F6] to-[#06B6D4] text-transparent bg-clip-text">
                Hook
            </span>{" "}
            🎬
            </h1>
            <p className="text-[#8B949E] mt-3 text-lg">
            Upload your video and get a full Hook Analysis powered by AI.
            </p>
        </div>
    
        {/* UPLOAD & VIDEO PREVIEW */}
        <div className="w-full max-w-2xl bg-[#161B22] border border-[#21262D] rounded-2xl shadow-lg p-6">
            {!preview ? (
            <label
                htmlFor="video-upload"
                className="cursor-pointer flex flex-col items-center border-2 border-dashed border-[#30363D] rounded-xl p-12 hover:border-[#3B82F6] transition-all"
            >
                <Upload className="w-12 h-12 text-[#3B82F6] mb-3" />
                <p className="text-[#8B949E] font-medium">
                Click to upload or drag & drop
                </p>
                <p className="text-xs text-[#6E7681]">MP4 / MOV — 100MB max</p>
                <input
                id="video-upload"
                type="file"
                accept="video/*"
                className="hidden"
                onChange={handleVideoUpload}
                />
            </label>
            ) : (
            <>
            <video
            src={preview}
            controls
            className="w-[70%] max-w-xl mx-auto rounded-xl border border-[#21262D] mb-4"
            />
            <button
            className="text-sm text-[#8B949E] hover:text-[#3B82F6] transition mb-4"
            onClick={() => {
                setVideo(null);
                setPreview(null);
                setResult(null);
            }}
            >
            ← Choose another video
            </button>

            <button
            onClick={handleAnalyze}
            disabled={loading}
            className={`w-full py-3 rounded-lg font-medium text-white flex items-center justify-center gap-2 transition ${
                loading
                ? "bg-[#30363D] cursor-not-allowed"
                : "bg-gradient-to-r from-[#3B82F6] to-[#06B6D4] hover:opacity-90 shadow-[0_0_15px_#3B82F6]/40"
            }`}
            >
            {loading ? (
                <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Analyzing...
                </>
            ) : (
                <>
                Analyze Hook
                <FiZap className="w-4 h-4" />
                </>
            )}
            </button>
        </>
        )}
    </div>
    
    {result && !error && (
        <div className="mt-12 max-w-4xl mx-auto w-full animate-fadeIn space-y-12">

        {/* ✅ Hook transcrit */}
        <div className="bg-[#161B22] border border-[#21262D] p-6 rounded-xl">
        <p className="text-[#3B82F6] text-xs uppercase tracking-widest mb-3 font-semibold">
            Transcribed Hook
        </p>
        <p className="italic text-lg leading-relaxed text-[#E6EDF3] border-l-2 border-[#3B82F6] pl-4">
            “{result.hook}”
        </p>
        </div>

        <h2 className="text-3xl font-bold text-center text-[#E6EDF3]">
        AI Hook Analysis
        </h2>

        {/* ✅ TEXT ANALYSIS */}
        <div className="bg-[#161B22] border border-[#21262D] p-6 rounded-xl">
        <h3 className="text-xl font-bold text-[#3B82F6] mb-6 flex items-center gap-2">
            <FiMic className="text-[#3B82F6]" />
            Text Analysis
        </h3>

        {/* ✅ Score bleu + grande taille */}
        <p className="text-6xl font-black text-[#3B82F6] drop-shadow-sm mb-4">
            {result.textAnalysis.score}
        </p>

        <p className="text-sm text-[#8B949E] leading-relaxed">
            {result.textAnalysis.feedback}
        </p>

        {result.textAnalysis.improvements?.length > 0 && (
            <ul className="list-disc ml-4 mt-4 text-sm space-y-1 text-[#8B949E]">
            {result.textAnalysis.improvements.map((imp, i) => (
                <li key={i}>{imp}</li>
            ))}
            </ul>
        )}

        {result.textAnalysis.example && (
            <blockquote className="mt-5 border-l-2 border-[#3B82F6] pl-4 italic text-sm text-[#E6EDF3]">
            “{result.textAnalysis.example}”
            </blockquote>
        )}
        </div>

        {/* ✅ VIDEO ANALYSIS */}
        <div className="bg-[#161B22] border border-[#21262D] p-6 rounded-xl">
        <h3 className="text-xl font-bold text-[#06B6D4] mb-6 flex items-center gap-2">
            <FiVideo className="text-[#06B6D4]" />
            Visual Analysis
        </h3>

        {/* ✅ Score cyan + grande taille */}
        <p className="text-6xl font-black text-[#06B6D4] drop-shadow-sm mb-4">
            {result.videoAnalysis.score}
        </p>

        <p className="text-sm text-[#8B949E] leading-relaxed">
            {result.videoAnalysis.feedback}
        </p>

        {result.videoAnalysis.improvements?.length > 0 && (
            <ul className="list-disc ml-4 mt-4 text-sm space-y-1 text-[#8B949E]">
            {result.videoAnalysis.improvements.map((imp, i) => (
                <li key={i}>{imp}</li>
            ))}
            </ul>
        )}
        </div>
    </div>
    )}
    </div>
);
}
